////////////////////////////////////////////
// API TYPES — SHAPES THE BACKEND RETURNS
////////////////////////////////////////////
//
// THE SERVER SERIALIZES BIGINTS AS STRINGS; rehydrate* BELOW RESTORES THE
// SAME FIELD SETS THE VANILLA util.js DID. FIELD NAMES WERE CAPTURED FROM
// LIVE /api/launches RESPONSES (2026-07-18) — THE DECODERS LIVE IN
// web/server/services/launch.service.mjs.

////////////////////////////////////////////
// SOLANA LAUNCH
////////////////////////////////////////////
export interface SolLaunchWire {
  address: string;
  authority: string;
  name: string;
  symbol: string;
  uri: string;
  status: number;
  launchId: string;
  commitStart: string;
  commitEnd: string;
  goal: string;
  tokensForSale: string;
  feeCollected: string;
  totalCommitted: string;
  clearingTotal: string;
  settledAt: string;
  deliveryDeposit: string;
  detonatedAt?: string;
  creatorPool?: string;
  creatorClaimed?: string;
  mint: string | null;
  feeBps?: number;
  feeRecipient?: string;
  feeSplitDone?: boolean;
  creatorShareBps?: number;
  creatorLockSecs?: number;
  vestingSecs?: number;
  maxWalletBps?: number;
  participantCount?: number;
  resolvedCount?: number;
  reserveReclaimed?: boolean;
  bump?: number;
  vaultBump?: number;
  meta?: LaunchMeta | null;
  kind?: "fair" | "instant";
}

// REHYDRATED — BIGINT FIELDS RESTORED FOR MATH/RENDER (SAME SET AS util.js)
export interface SolLaunch extends Omit<SolLaunchWire,
  "launchId" | "commitStart" | "commitEnd" | "goal" | "tokensForSale" |
  "feeCollected" | "totalCommitted" | "clearingTotal" | "settledAt" | "deliveryDeposit"> {
  launchId: bigint;
  commitStart: bigint;
  commitEnd: bigint;
  goal: bigint;
  tokensForSale: bigint;
  feeCollected: bigint;
  totalCommitted: bigint;
  clearingTotal: bigint;
  settledAt: bigint;
  deliveryDeposit: bigint;
}

const SOL_LAUNCH_BIGINTS = [
  "launchId", "commitStart", "commitEnd", "goal", "tokensForSale",
  "feeCollected", "totalCommitted", "clearingTotal", "settledAt", "deliveryDeposit",
] as const;

export function rehydrateLaunch(j: SolLaunchWire): SolLaunch {

  const out: Record<string, unknown> = { ...j };
  for (const k of SOL_LAUNCH_BIGINTS)
    if (j[k] != null) out[k] = BigInt(j[k]);
  return out as unknown as SolLaunch;
}

////////////////////////////////////////////
// ROBINHOOD CHAIN LAUNCH — address IS THE SYNTHETIC "0xfactory:id" KEY
////////////////////////////////////////////
export interface RhLaunchWire {
  address: string;
  authority: string;
  name: string;
  symbol: string;
  uri: string;
  status: number;
  goal: string;
  totalCommitted: string;
  commitEnd: string;
  tokenId: string;
  ignitedAt: string;
  token?: string | null;
  pool?: string | null;
  capBps?: number;
  creatorUnlockSecs?: number;
  participantCount?: number;
  resolvedCount?: number;
  launchFeeSettled?: boolean;
  creatorClaimed?: boolean;
  // DB-FOLDED CREATION TIME — PRESENT WHEN THE KEEPER HAS INDEXED THE LAUNCH
  createdAt?: string | number | null;
  meta?: LaunchMeta | null;
  kind?: "fair" | "instant";
  // INSTANT-LAUNCH DISCLOSURES (FuzeInstantV2 — DETAIL RESPONSES ONLY)
  liquidityWei?: string;
  devBuyWei?: string;
  tokensBought?: string;
}

export interface RhLaunch extends Omit<RhLaunchWire, "goal" | "totalCommitted" | "commitEnd" | "tokenId" | "ignitedAt"> {
  goal: bigint;
  totalCommitted: bigint;
  commitEnd: bigint;
  tokenId: bigint;
  ignitedAt: bigint;
}

const RH_LAUNCH_BIGINTS = ["goal", "totalCommitted", "commitEnd", "tokenId", "ignitedAt"] as const;

export function rehydrateRhLaunch(j: RhLaunchWire): RhLaunch {

  const out: Record<string, unknown> = { ...j };
  for (const k of RH_LAUNCH_BIGINTS)
    if (j[k] != null) out[k] = BigInt(j[k]);
  return out as unknown as RhLaunch;
}

// CHAIN DISCRIMINATOR — A WIRE LAUNCH'S address SHAPE PICKS ITS CHAIN
export function isRhWire(l: { address: string }): boolean {

  return l.address.startsWith("0x");
}

////////////////////////////////////////////
// AUCTION-DERIVED CURVE (V3) LAUNCH — key IS THE BARE FuzeAuction ADDRESS,
// NO COLON (see config/network.ts's RH_V3_KEY_RE / launchVenue). SHAPE FROM
// U-F1's read endpoint (docs/auction-curve-frontend-plan.md), WRITTEN AHEAD
// OF THAT UNIT AGAINST THE REAL auction_v3_launches ROW + ops/keeper-rh.mjs's
// scanAuctionV3/auctionV3EventColumns (verified 2026-07-27, U-F0) — U-F1's
// service layer (getAuctionV3State/listAuctionV3) MUST serialize to exactly
// this shape.
//
// NAMING CORRECTION vs the frontend plan's prose: the plan describes
// promoting the data blob's nested `curve` object to a top-level `curve`
// field, but auction_v3_launches ALREADY HAS a top-level `curve` COLUMN (the
// FuzeCurveV3 CONTRACT ADDRESS — see web/server/lib/db.mjs). Promoting the
// nested state object under the SAME name would silently clobber the
// address. The nested object is named `curveState` here instead; U-F1 must
// serialize data.curve -> curveState, not -> curve.
//
// FIELDS ARE OPTIONAL WHEREVER THE KEEPER MAY NOT HAVE BACKFILLED THEM YET
// (e.g. totalCommitted/commitEnd only exist after the first heartbeat poll
// post-LaunchCreated; curveState only exists once curve is non-null, i.e.
// post-settlement) — SAME "UNKNOWN = ABSENT, NEVER 0/NaN" DISCIPLINE AS
// RhLaunchWire's createdAt.
export interface AuctionV3CurveStateWire {
  X: string;
  Y: string;
  X0: string;
  exitLiquidity: string;
  graduated: boolean;
  poolMigrated: boolean;
  sunsetDeadline: string;
  creatorFeesClaimable: string;
  protocolFeesClaimable: string;
  // PRESENT ONLY AFTER PoolMigrated/Sunset (poolMigrated === true)
  migration?: "graduated" | "sunset";
  // PRESENT ONLY AFTER CurveGraduated
  finalX?: string;
  finalY?: string;
  ethRefunded?: string;
  // PRESENT ONLY AFTER PoolMigrated/Sunset
  liquidity?: string;
  // PRESENT ONLY AFTER Sunset (NOT CurveGraduated — a graduated curve burns nothing)
  tokensBurned?: string;
}

export interface AuctionV3Wire {
  auction: string; // THE LAUNCH KEY — BARE FuzeAuction ADDRESS
  factory: string;
  creator: string | null;
  curve: string | null; // FuzeCurveV3 CONTRACT ADDRESS — NOT curve state (see curveState)
  token: string | null;
  status: "open" | "settled" | "scrubbed";
  positionTokenId: string | null;
  createdAt?: string | number | null;
  name?: string;
  symbol?: string;
  aMax?: string;
  commitDuration?: string;
  totalCommitted?: string;
  commitEnd?: string;
  // PRESENT ONLY ONCE status !== "open" (LaunchSettled/LaunchScrubbed)
  auctionRaise?: string;
  // PRESENT ONLY ONCE status === "settled" (LaunchSettled)
  burned?: string;
  meta?: LaunchMeta | null;
  curveState?: AuctionV3CurveStateWire;
  // PRESENT ONLY ON DETAIL READS (handleLaunchState tags it; list rows get it
  // from handleLaunchList's own .map fold instead — see routes' own tagging)
  kind?: "auction-v3";
}

////////////////////////////////////////////
// INSTANT-CURVE (V3) LAUNCH — U-I6 (docs/instant-curve-build-plan.md). SAME
// FuzeCurveV3 CONTRACT/EVENTS AS AuctionV3Wire ABOVE (curveState is BYTE-FOR-
// BYTE THE SAME SHAPE, AuctionV3CurveStateWire, REUSED AS-IS — NOT REDEFINED
// — SEE web/server/services/launch.service.mjs's flattenInstantV3Row, WHICH
// BUILDS IT WITH THE IDENTICAL FIELD LIST AS flattenAuctionV3Row). THE REAL
// DIFFERENCE: NO AUCTION EXISTS AT ALL — key IS THE BARE FuzeCurveV3
// ADDRESS (curve), NOT auction (see db.mjs's own comment on why the DB table
// is keyed by curve, not token: both spring into existence atomically, and
// every crank/live-read field is curve-address-keyed already). NO
// aMax/commitDuration/totalCommitted/commitEnd/auctionRaise — THOSE ARE
// AUCTION-ONLY FIELDS WITH NO INSTANT-LAUNCH ANALOG. status IS A DIFFERENT
// ENUM: "trading" | "graduated" | "sunset" (THE CURVE'S OWN LIFECYCLE STAGE
// — INSTANT HAS NO SEPARATE AUCTION-VS-CURVE DIMENSION TO KEEP APART, UNLIKE
// AuctionV3Wire's status, WHICH TRACKS THE AUCTION'S LIFECYCLE ONLY).
export interface InstantV3Wire {
  curve: string; // THE LAUNCH KEY — BARE FuzeCurveV3 ADDRESS (no auction exists)
  factory: string;
  creator: string | null;
  token: string | null;
  status: "trading" | "graduated" | "sunset";
  positionTokenId: string | null;
  createdAt?: string | number | null;
  name?: string;
  symbol?: string;
  // THE CREATOR'S DEV-BUY (InstantLaunchCreated) — NOT AN "aMax"/RAISE, A
  // REAL FIRST TRADE. devBuyWei IS THE GROSS ETH SENT TO buy() ATOMICALLY
  // INSIDE instantLaunch(); tokensOut IS EXACTLY WHAT THAT TRADE BOUGHT.
  devBuyWei?: string;
  tokensOut?: string;
  burned?: string;
  meta?: LaunchMeta | null;
  curveState?: AuctionV3CurveStateWire;
  kind?: "instant-v3";
}

const INSTANT_V3_BIGINTS = ["devBuyWei", "tokensOut", "burned", "positionTokenId"] as const;

export interface InstantV3 extends Omit<InstantV3Wire, typeof INSTANT_V3_BIGINTS[number] | "curveState"> {
  devBuyWei?: bigint;
  tokensOut?: bigint;
  burned?: bigint;
  // ALWAYS PRESENT AS A KEY (NULLABLE), MIRRORING AuctionV3's OWN CONVENTION
  positionTokenId: bigint | null;
  curveState?: AuctionV3CurveState;
}

export function rehydrateInstantV3(j: InstantV3Wire): InstantV3 {

  const out: Record<string, unknown> = { ...j };
  for (const k of INSTANT_V3_BIGINTS)
    if (j[k] != null) out[k] = BigInt(j[k]!);
  if (j.curveState) {

    const cs: Record<string, unknown> = { ...j.curveState };
    for (const k of AUCTION_V3_CURVE_BIGINTS)
      if (j.curveState[k] != null) cs[k] = BigInt(j.curveState[k]!);
    out.curveState = cs;
  }
  return out as unknown as InstantV3;
}

const AUCTION_V3_CURVE_BIGINTS = [
  "X", "Y", "X0", "exitLiquidity", "sunsetDeadline", "creatorFeesClaimable", "protocolFeesClaimable",
  "finalX", "finalY", "ethRefunded", "liquidity", "tokensBurned",
] as const;

export interface AuctionV3CurveState extends Omit<AuctionV3CurveStateWire, typeof AUCTION_V3_CURVE_BIGINTS[number]> {
  X: bigint;
  Y: bigint;
  X0: bigint;
  exitLiquidity: bigint;
  sunsetDeadline: bigint;
  creatorFeesClaimable: bigint;
  protocolFeesClaimable: bigint;
  finalX?: bigint;
  finalY?: bigint;
  ethRefunded?: bigint;
  liquidity?: bigint;
  tokensBurned?: bigint;
}

const AUCTION_V3_BIGINTS = [
  "aMax", "commitDuration", "totalCommitted", "commitEnd", "auctionRaise", "burned", "positionTokenId",
] as const;

export interface AuctionV3 extends Omit<AuctionV3Wire, typeof AUCTION_V3_BIGINTS[number] | "curveState"> {
  aMax?: bigint;
  commitDuration?: bigint;
  totalCommitted?: bigint;
  commitEnd?: bigint;
  auctionRaise?: bigint;
  burned?: bigint;
  // ALWAYS PRESENT AS A KEY (NULLABLE), MIRRORING THE WIRE — NOT OPTIONAL,
  // SO A CALLER CAN'T FORGET TO HANDLE THE PRE-GRADUATION null CASE
  positionTokenId: bigint | null;
  curveState?: AuctionV3CurveState;
}

// REHYDRATE — SAME BIGINT-SET IDIOM AS rehydrateRhLaunch, PLUS ONE LEVEL FOR
// THE NESTED curveState. UNKNOWN/ABSENT STAYS ABSENT (NEVER COERCED TO 0n).
export function rehydrateAuctionV3(j: AuctionV3Wire): AuctionV3 {

  const out: Record<string, unknown> = { ...j };
  for (const k of AUCTION_V3_BIGINTS)
    if (j[k] != null) out[k] = BigInt(j[k]!);
  if (j.curveState) {

    const cs: Record<string, unknown> = { ...j.curveState };
    for (const k of AUCTION_V3_CURVE_BIGINTS)
      if (j.curveState[k] != null) cs[k] = BigInt(j.curveState[k]!);
    out.curveState = cs;
  }
  return out as unknown as AuctionV3;
}

////////////////////////////////////////////
// SHARED SHAPES
////////////////////////////////////////////

// HOSTED METADATA (POST /api/host-metadata PAYLOAD, meta ON LAUNCH READS)
export interface LaunchMeta {
  name?: string;
  symbol?: string;
  image?: string | null;
  banner?: string | null;
  description?: string | null;
  twitter?: string | null;
  telegram?: string | null;
  website?: string | null;
}

// GET /api/launch/<key>/position — ENRICHED PER-WALLET POSITION (BOTH
// CHAINS). LIVE HALF: amount/settled. LEDGER HALF: THE LIFETIME STORY —
// EVERY ENRICHED FIELD IS OPTIONAL/NULL WHEN THE LEDGER HASN'T INDEXED THE
// LAUNCH (SERVER CONTRACT: UNKNOWN = null, NEVER 0). AMOUNTS ARE STRINGS
// (RH WEI OVERFLOWS Number).
export interface Position {
  amount: string;
  settled: boolean;
  committed?: string | null;
  committedAt?: string | null;
  committedTx?: string | null;
  resolution?: "pending" | "claimed" | "refunded" | "delivered" | null;
  resolvedAt?: string | null;
  resolvedTx?: string | null;
  payout?: string | null;
  fee?: string | null;
  tokens?: string | null;
  source?: "live+ledger" | "live" | "ledger" | "chain";
}

// GET /api/wallet/<owner>/commits — ONE WALLET'S FAIR-LAUNCH COMMIT HISTORY,
// EACH ROW'S LAUNCH HYDRATED (SOLANA ONLY; SEE
// web/server/routes/launch.routes.mjs handleWalletCommits). BACKS THE "My
// launches" BOUGHT VIEW — SAME events-LEDGER DERIVATION AS Position ABOVE,
// TRANSPOSED TO ONE ROW PER LAUNCH THIS WALLET EVER COMMITTED TO (NOT JUST
// THE ONE launch/position READS). BOTH CHAINS — launch IS WHICHEVER WIRE
// SHAPE THE REQUESTED net's CHAIN SERVES (CALLERS DISCRIMINATE ON net, SAME
// AS useLaunches' LaunchListWire). AMOUNTS ARE STRINGS (POSTGRES NUMERICS;
// WEI ON RH, LAMPORTS ON SOLANA); committedAt/resolvedAt ARE ISO STRINGS;
// resolution IS THE RAW EVENT KIND, NOT Position's LOWERCASE resolution
// UNION — null UNTIL A RESOLUTION EVENT LANDS (SERVER CONTRACT: UNKNOWN =
// null, NEVER 0/""). RH NEVER PRODUCES 'Claimed' (NO SELF-CLAIM PATH — SEE
// getCommitHistoryRh's SERVER-SIDE COMMENT).
export interface ParticipationWire {
  launch: SolLaunchWire | RhLaunchWire;
  position: {
    committedLamports: string | null;
    committedAt: string | null;
    committedSig: string | null;
    resolution: "Claimed" | "Refunded" | "Delivered" | null;
    resolvedAt: string | null;
    resolvedSig: string | null;
    payout: string | null;
    fee: string | null;
    tokens: string | null;
  };
}

// GET /api/launch/<key>/audit — TRUST-GRID AGGREGATES. DERIVED BLOCKS ARE
// null (NEVER PARTIAL, NEVER ZEROS) WHEN THE LEDGER CAN'T ANSWER — THE
// GRID DEGRADES TO THE MECHANISM TICKS.
export interface Audit {
  address: string;
  net: string;
  kind: "fair" | "instant";
  buyers: number | null;
  totalCommitted: string | null;
  goal: string | null;
  concentration: {
    top10Pct: number;
    top10: { owner: string; committed: string; pct: number }[];
    uniqueCommitters: number;
    source: "ledger" | "live";
  } | null;
  perWalletCapPct: number | null;
  creatorSharePct: number | null;
  devBuy: { devBuyWei: string; liquidityWei: string; tokensBought: string; supplyPct: number } | null;
  fees: { completionFee: string | null; refundFees: string | null; launchFee: string | null; total: string } | null;
  resolved: { refunded: number; delivered: number; claimed: number; pending: number } | null;
  escrow: string | null;
  pool: string | null;
  attestUrl: string | null;
  // THE TOKEN-CREATION TX (Detonated/Ignited) — null UNTIL INDEXED
  createTx: string | null;
  updatedAt: string;
}

// GET /api/launch/<key>/creator — CREATOR EARNINGS/VESTING (PUBLIC DATA;
// THE CLIENT RENDERS IT ONLY FOR THE AUTHORITY'S OWN WALLET)
export interface CreatorPanelWire {
  chain: "solana" | "rh";
  authority: string;
  creatorPool?: string | null;
  creatorClaimed?: string | boolean | null;
  feeCollected?: string | null;
  launchFeeSettled?: boolean;
  creatorSharePct?: number;
  sweeps: { toCreator: string | null; toTreasury: string | null; tx: string; at: string | null }[];
  claims: { tokens: string | null; tx: string; at: string | null }[];
  vesting: {
    lockSecs?: number;
    dripSecs?: number;
    unlockSecs?: number;
    detonatedAt?: string | null;
    ignitedAt?: string | null;
    cliffAt?: string | null;
    unlockAt?: string | null;
    fullyVestedAt?: string | null;
    vestedNow?: string | null;
    claimableNow?: string | boolean | null;
  };
  ledgerSource: "ledger" | null;
}

// GET /api/score/<launch> — REPUTATION BADGES (SHAPE CONSUMED BY FuzeRep'S
// PORT IN STAGE 3; SERVER: web/server/scorer.mjs)
export interface Score {
  creator?: { level?: string; label?: string; detail?: string } | null;
  sybil?: { level?: string; label?: string; detail?: string; pct?: number } | null;
  [k: string]: unknown;
}

// GET /api/auth/me
export interface AuthMe {
  wallet?: string;
  role: "everybody" | "user" | "admin";
  alias?: string | null;
}

// POST /api/tx/*/build RESPONSES
export interface BuiltTx {
  txBase64: string;
  launch?: string;
  launchId?: string;
  end?: number;
  // pump instant-create (U-I5) — the fresh mint from /api/tx/instant-create/build,
  // and the dev-buy quote from /api/tx/instant-create/dev-buy/build.
  mint?: string;
  quote?: { tokensOut: string; minTokensOut: string };
  // pump instant-create (U-A5, docs/pump-instant-alt-plan.md) — true when
  // /api/tx/instant-create/build folded the 70/30 fee-share registration into
  // THIS tx (this net's ALT is configured). When true, the caller must NOT
  // also call /api/tx/instant-create/register-fees/build — pump-fees'
  // update_fee_shares_v2 revokes admin on its first call, so a second call
  // against an already-registered sharing_config fails on-chain.
  feesRegistered?: boolean;
}

// POST /api/tx/instant-create/confirm RESPONSE
export interface InstantConfirmResult {
  address: string;
}
